import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  usePlmPartnerDetailEditQuery,
  usePlmPartnerDetailEditSubSubscription,
} from '../../../../types/generated-types';
import { PlmPartnerDetailForm } from './plmPartner-detail-form';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

export const PlmPartnerEdit = () => {
  const { id } = useParams<{ id: string }>();
  const { setTitle, setTitlePath } = useInjectableComponents();

  const { data, loading, error } = usePlmPartnerDetailEditQuery({
    variables: { id: id! },
    skip: !id,
  });

  usePlmPartnerDetailEditSubSubscription({
    variables: { id: id! },
    skip: !id,
    onData: updateCacheFromSubscriptionEvent,
  });

  const isSmall = useIsSmallScreen();

  React.useEffect(() => {
    setTitle(
      isSmall ? 'Edit PLM Partner' : 'Edit Peak Load Management Partner',
    );
    setTitlePath('/plmPartners');
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Typography variant="h4" color="secondary">
          Preparing your data..
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">
          Error loading PLM partner: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <PlmPartnerDetailForm
      plmPartner={data?.plmPartnerById || undefined}
      loading={loading}
      error={error}
    />
  );
};
